import { UcoastVideo } from '@/scripts/core/ucoast-video'
import type {
	DebounceCallback,
	EventWithRelatedTarget,
	FocusableHTMLElement,
} from '@/scripts/types/theme'
import { MediaManager } from '@/scripts/core/media'
import { type ProductVariant } from '@/scripts/shopify'
import { type HeaderMenu } from '@/scripts/interactive-defer/header-menu'
import { toggleHeaderColorScheme } from '@/scripts/core/cart-functions'
import { type StickyHeader } from '@/scripts/theme/sticky-header'
import { type DynamicQuizStub } from '@/scripts/interactive-defer/dynamic-quiz-stub'
import { initializeScrollAnimationTrigger } from '@/scripts/theme/animations'
import { WaitlistPopup } from '@/scripts/interactive-defer/welcome-popup'
import type { EmblaCarouselType } from 'embla-carousel'
// CONSTANTS
export const ON_CHANGE_DEBOUNCE_TIMER = 300

export const PUB_SUB_EVENTS = {
	cartUpdate: 'cart-update',
	quantityUpdate: 'quantity-update',
	variantChange: 'variant-change',
	cartError: 'cart-error',
}

//  common attributes. sometimes these are used as elements selectors, if the attribute is added/removed
export const ATTRIBUTES = {
	cartEmpty: 'data-uc-cart-empty',
	loading: 'data-uc-loading',
	submenu: 'data-uc-submenu',
	menuOpening: 'data-uc-menu-opening',
}

// selectors for common elements where the selector won't be added/removed
export const SELECTORS = {
	cartLink: '[data-uc-cart-icon-bubble]',
	loadingOverlay: '[data-uc-loading-overlay]',
	loadingOverlaySpinner: '[data-uc-loading-overlay-spinner]',
	sectionHeader: '.section-header',
	headerWrapper: '[data-uc-header-wrapper]',
}

// PUB SUB
// todo: add additional data types as needed

export type CartUpdateEvent = {
	source: 'cart-items' | 'product-form'
	productVariantId?: number
}

export type CartErrorEvent = {
	source: 'product-form'
	productVariantId: number
	errors: string | string[] | { [key: string]: string[] }
	message: string
}

export type VariantChangeEvent = {
	source: 'product-form'
	data: {
		sectionId: string
		html: Document
		variant: ProductVariant
		productId: number
		fromExternal: boolean
	}
}

export type PubSubEvent =
	| CartUpdateEvent
	| CartErrorEvent
	| VariantChangeEvent
	| undefined

// typeguards for events

export function isCartUpdateEvent(obj: PubSubEvent): obj is CartUpdateEvent {
	if (!obj) return false
	if (!('source' in obj)) return false
	if (!('productVariantId' in obj)) return false
	return true
}

export function isCartErrorEvent(obj: PubSubEvent): obj is CartErrorEvent {
	if (!obj) return false
	if (!('source' in obj)) return false
	if (!('productVariantId' in obj)) return false
	if (!('message' in obj)) return false
	if (!('errors' in obj)) return false
	return true
}

export function createVariantChangeEvent(event: VariantChangeEvent) {
	if (!isVariantChangeEvent(event)) {
		throw new Error('Event is not a VariantChangeEvent')
	}
	return event
}

export function isVariantChangeEvent(
	obj: PubSubEvent
): obj is VariantChangeEvent {
	if (!obj) return false
	if (!('data' in obj)) return false
	if (!('sectionId' in obj.data)) return false
	if (!('html' in obj.data)) return false
	if (!('variant' in obj.data)) return false
	return true
}

export type SubscriberCallback = (pubSubEvent: PubSubEvent) => void

let subscribers: Record<string, SubscriberCallback[]> = {}

export function subscribe(eventName: string, callback: SubscriberCallback) {
	if (subscribers[eventName] === undefined) {
		subscribers[eventName] = []
	}

	subscribers[eventName] = [...subscribers[eventName], callback]

	return function unsubscribe() {
		subscribers[eventName] = subscribers[eventName].filter((cb) => {
			return cb !== callback
		})
	}
}

export function publish(
	eventName: string,
	pubSubEvent: PubSubEvent = undefined
) {
	if (subscribers[eventName]) {
		subscribers[eventName].forEach((callback) => {
			callback(pubSubEvent)
		})
	}
}

// FUNCTIONS.ts

// local types

type HTMLElementProperty =
	| 'parentNode'
	| 'firstChild'
	| 'lastChild'
	| 'previousSibling'
	| 'nextSibling'
	| 'firstElementChild'
	| 'lastElementChild'
	| 'previousElementSibling'
	| 'nextElementSibling'
	| 'parentElement'

type CommonEventType = MouseEvent | KeyboardEvent | TouchEvent

// begin typescript DOM functions
// this is a set of functions I wrote while refactoring Dawn 9.0 to typescript
// ended up being very flexible and useful for enforcing types when accessing dom elements so we are reusing for Dawn 10.0

export const qsRequired = <
	T extends HTMLElement = HTMLElement,
	U extends HTMLElement = T,
>(
	selector: string,
	component?: HTMLElement,
	additionalProperty?: HTMLElementProperty
) => {
	const container = component ?? document
	const element = container.querySelector(selector) as T | null
	if (!element) throw new Error(`required element not found: ${selector}`)
	if (!additionalProperty) return element
	const elFromProperty = element[additionalProperty] as U | null
	if (!elFromProperty)
		throw new Error(`required element not found: ${selector}`)
	return elFromProperty
}

export const qsRequiredFromDocument = <T extends HTMLElement = HTMLElement>(
	selector: string,
	parsedDocument: Document
) => {
	const element = parsedDocument.querySelector(selector) as T | null
	if (!element) throw `required element not found: ${selector}`
	return element
}

// required querySelectorAll
export const qsaRequired = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const elements = container.querySelectorAll(selector) as NodeListOf<T>
	if (!elements.length)
		throw new Error(`required element group not found: ${selector}`)
	return elements
}

// return undefined if the HTML element isn't found
export const qsOptional = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const element = container.querySelector(selector) as T | null
	return element ?? undefined
}

// optional querySelectorAll typecast
export const qsaOptional = <T extends HTMLElement = HTMLElement>(
	selector: string,
	component?: HTMLElement | Document
) => {
	const container = component ?? document
	const elements = container.querySelectorAll(selector) as NodeListOf<T>
	if (!elements.length) return undefined
	return elements
}

// required event targets
export const currentTargetRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement,
>(
	event: E
) => {
	const element = event.currentTarget as (EventTarget & T) | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	return element
}

export const relatedTargetRequired = <
	E extends EventWithRelatedTarget = EventWithRelatedTarget,
	T extends HTMLElement = HTMLElement,
>(
	event: E
) => {
	const element = event.relatedTarget as (EventTarget & T) | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	return element
}

export const relatedTargetOptional = <
	E extends EventWithRelatedTarget = EventWithRelatedTarget,
	T extends HTMLElement = HTMLElement,
>(
	event: E
) => {
	return (event.relatedTarget as EventTarget & T) ?? undefined
}

export const targetRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement | Document = HTMLElement,
>(
	event: E
) => {
	const element = event.target as T | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	return element
}

// optional event targets. this is sort of not important but will probably make type casting easier
export const currentTargetOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement,
>(
	event: E
) => {
	return (event.currentTarget as EventTarget & T) ?? undefined
}

export const targetOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement,
>(
	event: E
) => {
	return (event.target as EventTarget & T) ?? undefined
}

export const targetClosestRequired = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement,
>(
	event: E,
	selector: string
) => {
	const element = event.target as T | null
	if (!element)
		throw new Error(`required element not found: event.currentTarget`)
	const closest = element.closest(selector) as T
	if (!closest)
		throw new Error(
			`required element not found: ${element.nodeName}.closest(${selector})`
		)
	return closest
}

export const targetClosestOptional = <
	E extends Event = CommonEventType,
	T extends HTMLElement = HTMLElement,
>(
	event: E,
	selector: string
) => {
	const element = event.target as T | null
	if (!element) return undefined
	const closest = element.closest(selector) as T
	if (!closest) return undefined
	return closest
}

// properties and methods of HTML elements that return an HTMLElement
export const closestRequired = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	selector: string
) => {
	const closest = element.closest(selector) as T
	if (!closest)
		throw new Error(
			`required element not found: ${element.nodeName}.closest(${selector})`
		)
	return closest
}

export const closestOptional = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	selector: string
) => {
	return (element.closest(selector) as T) ?? undefined
}

export const elementPropertyRequired = <T extends HTMLElement = HTMLElement>(
	element: HTMLElement,
	property: HTMLElementProperty
) => {
	const parent = element[property] as T | null
	if (!parent)
		throw new Error(
			`required element not found: ${element.nodeName}.parentNode`
		)
	return parent
}

// this function is use to safely define a new custom element
// it allows callbacks to be run before and after the element is defined -
// callback functionality is mainly for shop filters but can apply to any class with heavy usage of static methods

interface CustomElementConstructorWithStaticTagName
	extends CustomElementConstructor {
	htmlSelector: string
}

// throw error if something goes wrong with a constructor
export const safeDefineElement = (
	elementClass: CustomElementConstructorWithStaticTagName,
	callbackBefore?: Function,
	callbackAfter?: Function
): void => {
	try {
		if (customElements.get(elementClass.htmlSelector)) return
		if (callbackBefore instanceof Function) {
			const elsInDocument = qsaOptional(elementClass.htmlSelector)
			if (elsInDocument) {
				callbackBefore()
			}
		}
		customElements.define(elementClass.htmlSelector, elementClass)
		if (callbackAfter instanceof Function) {
			const elsInDocument = qsaOptional(elementClass.htmlSelector)
			if (elsInDocument) {
				callbackAfter()
			}
		}
	} catch (error) {
		console.error(
			`Failed to define custom element '${elementClass.htmlSelector}': ${error.message}`
		)
	}
}

// formData helpers
export const getOrThrow = <T extends FormData | Map<any, any> = FormData>(
	data: T,
	key: string
) => {
	const value = data.get(key)
	if (!value) throw new Error(`Value ${key} not found in ${data}`)
	return value
}
export const getOrUndefined = <T extends FormData | Map<any, any> = FormData>(
	data: T,
	key: string
) => {
	const value = data.get(key)
	if (!value) return undefined
	return value
}

// get attribute from element or throw error
export const getAttributeOrThrow = (attribute: string, el: HTMLElement) => {
	const data = el.getAttribute(attribute)
	if (!data)
		throw new Error(`Attribute ${attribute} no found on element ${{ el }}`)
	return data
}
export const getAttributeOrUndefined = (attribute: string, el: HTMLElement) => {
	const data = el.getAttribute(attribute)
	if (!data) return undefined
	return data
}

// scroll to anchor link with some ucoast-specific features, not used in default dawn repack
export const scrollToAnchor = (selector: string, handle?: string) => {
	const header = qsRequired('[data-uc-header-wrapper]')
	const productStickyNav = qsOptional('product-sticky-nav')
	const anchor = qsRequired(selector)
	const headerHeight = productStickyNav
		? productStickyNav.offsetHeight
		: header.offsetHeight
	const collectionTop = anchor.getBoundingClientRect().top
	const scrollToPosition =
		collectionTop -
		headerHeight -
		parseFloat(
			window?.getComputedStyle(anchor)?.getPropertyValue('margin-top') ??
				0
		) +
		1
	window.scrollBy({ top: scrollToPosition, behavior: 'smooth' })
	if (!handle) return
	const focusable = qsOptional(`[data-anchor-focus="${handle}"]`)
	if (focusable) focusable.focus({ preventScroll: true })
}

function setupScrollAnchors() {
	const anchors = qsaOptional<HTMLButtonElement>('[data-scroll-trigger]')
	anchors?.forEach((button) => {
		const handle = getAttributeOrThrow('data-scroll-trigger', button)
		button.addEventListener('click', (e) => {
			e.preventDefault()

			const area = qsOptional(`[data-scroll-anchor="${handle}"]`)
			const stickyHeader = qsRequired<StickyHeader>('sticky-header')
			if (area) {
				scrollToAnchor(`[data-scroll-anchor="${handle}"]`, handle)
				stickyHeader.beforeAnchorScroll()
			}
		})
	})
}

function setQuizTriggerListeners(trigger: HTMLElement) {
	trigger.addEventListener('click', (e) => {
		e.preventDefault()
		const quiz = qsRequired<DynamicQuizStub>('dynamic-quiz-stub')
		void quiz.initializePopup()
	})
	trigger.addEventListener('mouseover', (_) => {
		const quiz = qsRequired<DynamicQuizStub>('dynamic-quiz-stub')
		void quiz.preload()
	})
}

export function setupQuizTriggers(el?: HTMLElement) {
	const triggers = qsaOptional('[data-quiz-opener], a[href="#quiz"]', el)
	triggers?.forEach((trigger) => {
		setQuizTriggerListeners(trigger)
	})
}

// TODO: refactor to use app proxy for security
export const getBackendRoute = () => {
	return 'http://localhost:5000'
}

// accessibility functions

export function initializeSummaryA11y() {
	// this is from shopify
	// it adds some accessibility features to summary elements
	document
		.querySelectorAll('[id^="Details-"] summary:not([data-summary-hover])')
		.forEach((summary) => {
			summary.setAttribute('role', 'button')
			const parentNode =
				summary.parentNode instanceof HTMLElement
					? summary.parentNode
					: undefined
			const ariaExpandedVal =
				parentNode && parentNode.hasAttribute('open') ? 'true' : 'false'
			summary.setAttribute('aria-expanded', ariaExpandedVal)

			const nextElementSibling =
				summary.nextElementSibling instanceof HTMLElement
					? summary.nextElementSibling
					: undefined

			if (nextElementSibling && nextElementSibling.hasAttribute('id')) {
				summary.setAttribute(
					'aria-controls',
					getAttributeOrThrow('id', nextElementSibling)
				)
			}
			summary.addEventListener('click', (event) => {
				//event.preventDefault()
				const currentTarget = currentTargetRequired(event)
				const closestTarget = targetClosestRequired(event, 'details')
				const shouldOpen = !closestTarget.hasAttribute('open')
				currentTarget.setAttribute('aria-expanded', `${shouldOpen}`)
			})
			if (summary.closest('header-drawer, menu-drawer')) return

			const parentElement =
				summary.parentElement instanceof HTMLElement
					? summary.parentElement
					: undefined
			if (!parentElement) return
			parentElement.addEventListener('keyup', onKeyUpEscape)
		})

	document.querySelectorAll('[data-summary-hover="off"]').forEach((el) => {
		el.addEventListener('mouseenter', (_) => {
			closeAllHeaderMenus()
		})
	})

	const closeOnExitContainers = qsaOptional(
		'[data-close-menus-on-mouse-exit]'
	)
	closeOnExitContainers?.forEach((el) => {
		el.addEventListener('mouseleave', (_) => {
			closeAllHeaderMenus()
		})
	})
}

export function closeAllHeaderMenus(skipAnimation = false) {
	if (!window.Ucoast.openMenuId) return
	if (!skipAnimation) {
		toggleHeaderColorScheme('starting')
	}
	const headerMenus = qsaOptional<HeaderMenu>('header-menu')
	headerMenus?.forEach((el) => el.close())
	window.Ucoast.openMenuId = undefined
	document.body.classList.remove('megamenu-open')
}

export function getFocusableElements(
	container: HTMLElement
): FocusableHTMLElement[] {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	)
}

export function focusVisiblePolyfill() {
	const navKeys = [
		'ARROWUP',
		'ARROWDOWN',
		'ARROWLEFT',
		'ARROWRIGHT',
		'TAB',
		'ENTER',
		'SPACE',
		'ESCAPE',
		'HOME',
		'END',
		'PAGEUP',
		'PAGEDOWN',
	]
	let currentFocusedElement: HTMLElement | Element | null = null
	let mouseClick: boolean | null = null

	window.addEventListener('keydown', (event) => {
		if (navKeys.includes(event.code?.toUpperCase())) {
			mouseClick = false
		}
	})

	window.addEventListener('mousedown', (_event: MouseEvent) => {
		mouseClick = true
	})

	window.addEventListener(
		'focus',
		() => {
			if (currentFocusedElement)
				currentFocusedElement.classList.remove('focused')

			if (mouseClick) return

			currentFocusedElement = document.activeElement
			if (currentFocusedElement instanceof HTMLElement) {
				currentFocusedElement.classList.add('focused')
			}
		},
		true
	)
}

export function onKeyUpEscape(event: KeyboardEvent) {
	if (event.code?.toUpperCase() !== 'ESCAPE') return

	const openDetailsElement = targetClosestOptional(event, 'details[open]')
	if (!openDetailsElement) return

	const summaryElement = qsRequired('summary', openDetailsElement)
	openDetailsElement.removeAttribute('open')
	summaryElement.setAttribute('aria-expanded', 'false')
	summaryElement.focus()
}

export function pauseAllMedia() {
	const jsYoutubeEls = qsaOptional<HTMLIFrameElement>('.js-youtube')
	if (jsYoutubeEls) {
		jsYoutubeEls.forEach((video) => {
			if (!video.contentWindow) return
			video.contentWindow.postMessage(
				'{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
				'*'
			)
		})
	}

	const jsVimeoEls = qsaOptional<HTMLIFrameElement>('.js-vimeo')
	if (jsVimeoEls) {
		jsVimeoEls.forEach((video) => {
			if (!video.contentWindow) return
			video.contentWindow.postMessage('{"method":"pause"}', '*')
		})
	}

	const html5Videos = qsaOptional<HTMLVideoElement>('video')
	if (html5Videos) {
		html5Videos.forEach((video) => video.pause())
	}
}

// event based functions

export function debounce(fn: Function, wait: number): DebounceCallback {
	let timeoutId: number
	return (...args) => {
		clearTimeout(timeoutId)
		// @ts-ignore
		// since this is a generic function that can literally be used for anything, I think 'any' is the best type here
		// 'unknown' would be better but we can't assign a type without moving off of arrow functions
		timeoutId = setTimeout(() => fn.apply(this, args), wait)
	}
}
export function throttle(fn: Function, delay: number = 0) {
	let lastCall = 0
	return function (...args: unknown[]) {
		const now = new Date().getTime()
		if (now - lastCall < delay) {
			return
		}
		lastCall = now
		return fn(...args)
	}
}

// fetch API configs

export function fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/${type}`,
		},
	}
}

type AddToCartFormValues = {
	items: { quantity: number; id: number }[]
	form_type: string
	sections?: string
	sections_url?: string
}

export function addToCartConfig(body: FormData) {
	const quantity = getOrUndefined(body, 'quantity')
		? parseInt(getOrThrow(body, 'quantity'))
		: 1
	const data: AddToCartFormValues = {
		items: [
			{
				quantity,
				id: parseInt(getOrThrow(body, 'id')),
			},
		],
		form_type: getOrThrow(body, 'form_type'),
		sections: getOrUndefined(body, 'sections'),
		sections_url: getOrUndefined(body, 'sections_url'),
	}
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/json`,
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify(data),
	}
}

export function formatPhoneNumber(unchecked_number: string) {
	let phone_number = unchecked_number
		.replace('(', '')
		.replace(')', '')
		.replace('+', '')
		.replaceAll('-', '')
		.replaceAll(' ', '')
		.trim()
	phone_number = `+1${phone_number}`
	return phone_number
}

// this is a default shopify function that normally runs in global scope
// I moved it to functions for clarity

// trapFocusHandlers variable and associated functions are part of the global scope
// this function should always run first

const trapFocusHandlers: {
	focusin?: (event: Event) => void
	focusout?: (event: Event) => void
	keydown?: (event: KeyboardEvent) => void
} = {}

export function trapFocus(
	container: HTMLElement,
	elementToFocus: HTMLElement | undefined = container
) {
	const elements = getFocusableElements(container)
	const first = elements[0]
	const last = elements[elements.length - 1]

	removeTrapFocus()

	trapFocusHandlers.focusin = (event) => {
		if (
			event.target !== container &&
			event.target !== last &&
			event.target !== first
		)
			return

		if (!trapFocusHandlers.keydown)
			throw new Error(
				'trapFocusHandlers.focusin called before .keydown is defined'
			)

		document.addEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.focusout = function () {
		if (!trapFocusHandlers.keydown)
			throw new Error(
				'trapFocusHandlers.focusout called before .keydown is defined'
			)
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	trapFocusHandlers.keydown = function (event: KeyboardEvent) {
		if (event.code?.toUpperCase() !== 'TAB') return // If not TAB key
		// On the last focusable element and tab forward, focus the first element.
		const target = targetRequired<KeyboardEvent, FocusableHTMLElement>(
			event
		)
		if (target === last && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if (
			(event.target === container || event.target === first) &&
			event.shiftKey
		) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', trapFocusHandlers.focusout)
	document.addEventListener('focusin', trapFocusHandlers.focusin)

	elementToFocus.focus()

	if (
		elementToFocus instanceof HTMLInputElement &&
		['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
		elementToFocus.value
	) {
		elementToFocus.setSelectionRange(0, elementToFocus.value.length)
	}
}

// don't know why shopify put this function below the above but I'm not about to go debug it

export function removeTrapFocus(
	elementToFocus: HTMLElement | undefined = undefined
) {
	if (trapFocusHandlers.focusin) {
		document.removeEventListener('focusin', trapFocusHandlers.focusin)
	}
	if (trapFocusHandlers.focusout) {
		document.removeEventListener('focusout', trapFocusHandlers.focusout)
	}

	if (trapFocusHandlers.keydown) {
		document.removeEventListener('keydown', trapFocusHandlers.keydown)
	}

	if (elementToFocus) elementToFocus.focus()
}

export function getDistanceFromDocumentTop(
	element: Element,
	scrollTop: number
) {
	const rect = element.getBoundingClientRect()
	return rect.top + scrollTop
}

export function openWaitlistModal(
	url: string,
	variantId: number,
	opener: HTMLElement
) {
	const modal = qsRequired<WaitlistPopup>(`waitlist-popup`)
	void modal.openPopup(url, variantId, opener)
}
export function initializeShopifyConsentAPI() {
	window.Shopify?.loadFeatures(
		[
			{
				name: 'consent-tracking-api',
				version: '0.1',
			},
		],
		(error) => {
			if (error) {
				console.error('error initializing privacy api')
				console.log('error', error)
			} else {
				window.Ucoast.shopifyConsentAPILoaded = true
			}
		}
	)
}

function initGlobalUcoast() {
	safeDefineElement(UcoastVideo)
	const iOS = window.Ucoast?.iOS ?? false
	const Ucoast: typeof window.Ucoast = {
		shopifyConsentAPILoaded: false,
		iOS,
		editor: {
			selectedBlock: undefined,
		},
		screen: {
			width: window.innerWidth,
			height: window.innerHeight,
			portrait: window.innerHeight > window.innerWidth,
		},
		mediaManager: new MediaManager(),
	}
	if (!window.Ucoast) {
		window.Ucoast = Ucoast
	}
	if (!window.Ucoast.mediaManager) {
		window.Ucoast.mediaManager = new MediaManager()
	}
}

function setupInputFocusWorkaround() {
	document.addEventListener('DOMContentLoaded', function () {
		const viewport = qsRequired('meta[name=viewport]')

		function adjustViewport(zoomEnabled: boolean) {
			const scaleSettings = zoomEnabled
				? '1.0, user-scalable=1'
				: '1.0, user-scalable=0'
			viewport.setAttribute(
				'content',
				`width=device-width, initial-scale=1.0, maximum-scale=${scaleSettings}`
			)
		}

		document.addEventListener(
			'focusin',
			function (event) {
				const target = targetOptional(event)
				if (!target) return
				if (
					target.tagName === 'INPUT' ||
					target.tagName === 'SELECT' ||
					target.tagName === 'TEXTAREA'
				) {
					adjustViewport(false)
				}
			},
			true
		)
		document.addEventListener(
			'focusout',
			function (event) {
				const target = targetOptional(event)
				if (!target) return
				if (
					target.tagName === 'INPUT' ||
					target.tagName === 'SELECT' ||
					target.tagName === 'TEXTAREA'
				) {
					adjustViewport(false)
				}
			},
			true
		)

		document.addEventListener(
			'blur',
			function (event) {
				const target = targetOptional(event)
				if (
					target.tagName === 'INPUT' ||
					target.tagName === 'SELECT' ||
					target.tagName === 'TEXTAREA'
				) {
					adjustViewport(true)
				}
			},
			true
		)
	})
}

export function setOverflowStatus(container?: HTMLElement) {
	const el = container ?? document.body
	const overflowEls = qsaOptional<HTMLElement>('[data-overflow]', el)
	overflowEls?.forEach((el) => {
		//element.scrollHeight > element.clientHeight
		if (el.scrollHeight > el.clientHeight) {
			el.setAttribute('data-overflow', 'true')
		} else if (el.scrollWidth > el.clientWidth) {
			el.setAttribute('data-overflow', 'true')
		} else {
			el.setAttribute('data-overflow', 'false')
		}
	})
}

export const addToggleThumbBtnsActive = (
	emblaApiMain: EmblaCarouselType,
	emblaApiThumb: EmblaCarouselType
): (() => void) => {
	const slidesThumbs = emblaApiThumb.slideNodes()

	const toggleThumbBtnsState = (): void => {
		emblaApiThumb.scrollTo(emblaApiMain.selectedScrollSnap())
		const previous = emblaApiMain.previousScrollSnap()
		const selected = emblaApiMain.selectedScrollSnap()
		slidesThumbs[previous].classList.remove('embla-thumbs__slide--selected')
		slidesThumbs[selected].classList.add('embla-thumbs__slide--selected')
	}

	emblaApiMain.on('select', toggleThumbBtnsState)
	emblaApiThumb.on('init', toggleThumbBtnsState)

	return (): void => {
		const selected = emblaApiMain.selectedScrollSnap()
		slidesThumbs[selected].classList.remove('embla-thumbs__slide--selected')
	}
}

const debouncedHandleResize = debounce(handleResize, 50)

function handleResize() {
	let hasReloaded = false
	if (
		window.innerWidth !== window.Ucoast.screen.width &&
		window.innerHeight === window.Ucoast.screen.height
	) {
		// screen width updates only
		if (window.innerWidth < 990 && window.Ucoast.screen.width >= 990) {
			// screen width has decreased below 990
			void window.Ucoast.mediaManager.reloadAllDangerously()
			hasReloaded = true
		}
		if (window.innerWidth >= 990 && window.Ucoast.screen.width < 990) {
			// screen width has increased to 990 or above
			void window.Ucoast.mediaManager.reloadAllDangerously()
			hasReloaded = true
		}
	}
	if (
		window.innerHeight !== window.Ucoast.screen.height &&
		window.innerWidth === window.Ucoast.screen.width
	) {
		// screen height updates only
	}
	if (
		window.innerWidth !== window.Ucoast.screen.width ||
		window.innerHeight !== window.Ucoast.screen.height
	) {
		// update on every change
		setOverflowStatus()
	}
	if (
		window.innerWidth !== window.Ucoast.screen.width &&
		window.innerHeight !== window.Ucoast.screen.height
	) {
		if (!hasReloaded) {
			const portrait = window.innerHeight > window.innerWidth
			if (portrait !== window.Ucoast.screen.portrait) {
				console.log('scary reload')
				void window.Ucoast.mediaManager.reloadAllDangerously()
				hasReloaded = true
			}
		}
	}

	window.Ucoast.screen.width = window.innerWidth
	window.Ucoast.screen.height = window.innerHeight
	window.Ucoast.screen.portrait = window.innerHeight > window.innerWidth
}



export function globalSetup() {
	initGlobalUcoast()
	initializeShopifyConsentAPI()
	safeDefineElement(UcoastVideo)
	initializeSummaryA11y()
	setupScrollAnchors()
	setupQuizTriggers()
	try {
		document.querySelector(':focus-visible')
	} catch (e) {
		focusVisiblePolyfill()
	}
	// mediaLoader
	void window.Ucoast.mediaManager.initialLoad()
	window.addEventListener('DOMContentLoaded', () =>
		initializeScrollAnimationTrigger()
	)

	document.addEventListener('shopify:section:load', (event: Event) => {
		initializeScrollAnimationTrigger(targetRequired<Event, Document>(event))
	})
	window.addEventListener('resize', debouncedHandleResize)
	setupInputFocusWorkaround()
}
